import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import React, { memo, useMemo, useState, useCallback, useRef, useEffect, Fragment, useContext } from 'react';
import { createUseStyles, Loader } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from "../../../../lib/Web5Context";
import { getList, searchValuesByIds } from "../../../../lib/services/valueLists";
import { showErrorToast } from "../../../../lib/errors";
import { getUniqueKeys, sort } from "../../../../utils/array";
import ImportContext from "./../../ImportContext";
import SectionList from "../../../../components/SectionList";

// Custom components
import FuzzyFieldFormatter from "../formatters/FuzzyFieldFormatter";
import InnerClassFormatter from "../formatters/InnerClassFormatter";
import SwitchFormatter from "../formatters/SwitchFormatter";
import ExcelField from "./headerComponents/ExcelField";
import ForceField from "./headerComponents/ForceField";
import styles from "./styles";
const useStyles = createUseStyles(styles, 'MatchingList');
var _ref3 = /*#__PURE__*/React.createElement(Loader, {
  size: "big"
});
const MatchingList = _ref => {
  let {
    data,
    fields,
    fieldTable,
    canCreate,
    onMatch,
    hideMatchedFlields,
    matchBy
  } = _ref;
  const classes = useStyles();
  const {
    t,
    language: lang
  } = useWeb5Context();
  const {
    language: externalLanguage
  } = useContext(ImportContext);
  const language = externalLanguage || lang;
  const [isLoading, setIsLoading] = useState(false);
  const options = useRef(null);
  const matchValues = useRef(fields.map(field => data.extraInfo[field]));
  const matchOptions = useRef(fields.map(field => {
    var _data$extraInfo$field;
    return {
      label: field,
      value: (_data$extraInfo$field = data.extraInfo[field]) === null || _data$extraInfo$field === void 0 ? void 0 : _data$extraInfo$field.toString()
    };
  }));
  const [createAsNewOneObj, setCreateAsNewOne] = useState(fields.reduce((obj, current) => {
    obj[current] = false;
    return obj;
  }, {}));
  useEffect(() => {
    if (options.current || isLoading) return;
    setIsLoading(true);
    const ids = matchValues.current.reduce((arr, el) => el > 0 ? [...arr, el] : arr, []);
    Promise.all([searchValuesByIds({
      entity: fieldTable,
      ids,
      lang: language,
      searchType: parseInt(matchBy, 10)
    }), getList({
      list: fieldTable,
      language,
      searchType: matchBy
    })]).then(_ref2 => {
      let [{
        data: selectedOptions = []
      }, {
        data: firstOptions = []
      }] = _ref2;
      options.current = sort(getUniqueKeys([...Object.values(selectedOptions), ...firstOptions].map(item => ({
        label: item.description,
        value: item.id
      })), 'value'), 'label');
    }).catch(err => {
      options.current = [];
      console.error(err);
      showErrorToast(t, err);
    }).finally(() => setIsLoading(false));
  }, [fieldTable, isLoading, language, t, matchBy, canCreate]);
  const onChangeMatch = useCallback((index, value) => {
    matchValues.current[index] = value;
    onMatch(fields[index], value);
  }, [fields, onMatch]);
  const onChangeCreateAsNew = useCallback((index, value) => {
    matchValues.current[index] = value;
    onMatch(fields[index], value);
    setCreateAsNewOne(_objectSpread(_objectSpread({}, createAsNewOneObj), {}, {
      [fields[index]]: value
    }));
  }, [fields, onMatch, createAsNewOneObj]);
  const listProps = useMemo(() => {
    let columns = [{
      field: 'name',
      headerName: t('cfm_label_your_values_xls'),
      resizable: true,
      cellRenderer: 'InnerClassFormatter',
      cellRendererParams: {
        className: classes.matchingListCell
      },
      headerComponent: 'ExcelField'
    }, {
      field: 'nameField',
      headerName: t('cfm_label_fm_values'),
      resizable: true,
      cellRenderer: 'FuzzyFieldFormatter',
      cellRendererParams: {
        className: classes.fmCell,
        options: options.current,
        matchOptions: matchOptions.current,
        onChange: onChangeMatch,
        hasValuesSync: false,
        fieldTable
      },
      headerComponent: 'ForceField'
    }];
    if (canCreate) {
      columns.push({
        field: 'createAsNewOne',
        headerName: t('cfm_label_new_value'),
        resizable: true,
        cellRenderer: 'SwitchFormatter',
        cellRendererParams: {
          onChange: onChangeCreateAsNew
        }
      });
    }
    return {
      className: classes.matchingList,
      isLoading,
      rows: fields.map((field, index) => ({
        name: field,
        nameField: data.extraInfo[field],
        index,
        id: index,
        createAsNewOne: createAsNewOneObj[field]
      })).filter(field => hideMatchedFlields ? field.nameField === -1 : true),
      columns,
      components: {
        ExcelField,
        ForceField,
        InnerClassFormatter,
        FuzzyFieldFormatter,
        SwitchFormatter
      },
      options: {
        suppressRowHoverHighlight: true,
        rowHeight: 40,
        headerHeight: 50
      }
    };
  }, [classes.matchingList, classes.matchingListCell, classes.fmCell, t, onChangeMatch, canCreate, fieldTable, isLoading, fields, data.extraInfo, hideMatchedFlields, createAsNewOneObj, onChangeCreateAsNew]);
  return /*#__PURE__*/React.createElement(Fragment, null, (isLoading || !options.current) && /*#__PURE__*/React.createElement("div", {
    className: classes.modalLoader
  }, _ref3), !isLoading && options.current && /*#__PURE__*/React.createElement(SectionList, _extends({
    id: "matching-list"
  }, listProps)));
};
export default /*#__PURE__*/memo(MatchingList);